import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Button,
  ButtonGroup,
  Dropdown,
  Table,
  Pagination,
} from "react-bootstrap";
import { FaRedo, FaTrash } from "react-icons/fa";
import { AiOutlineLeft, AiOutlineRight } from "react-icons/ai";
import axios from "axios";
import * as XLSX from "xlsx";
import Footer from "../../../footer.js";
import InvoiceModal from "./invoiceModal.js";
import CreditMemo from "./creditMemo.js";
import Payment from "./payment.js";
import FilterModel from "./filterModel.js";
// import DeleteInvoicesModal from "./DeleteInvoicesModal.js"; // NEW
import Header from "../../../header/header.js";
import Navvbar from "../../../header/Navvbar.js";
import { useCurrency } from "../../../../context/currencyContext";
import FinanceSidebar from "../Financesidebar.js";
import DeleteInvoicesModal from "./Deleteinvoicesmodal";

// =========================================================
// CONFIG
// =========================================================
const ENTRIES_PER_PAGE = 20;
const API_BASE = "https://hms.backend.hiragroupcloud.com/api";

const Posting = () => {
  const { formatAmount } = useCurrency();

  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showFilterModel, setShowFilterModel] = useState(false);
  const [showCreditMemoModal, setShowCreditMemoModal] = useState(false);
  const [showReceivePaymentModal, setShowReceivePaymentModal] = useState(false);

  // NEW: delete-invoices modal state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [allPostings, setAllPostings] = useState([]);
  const [filteredPostings, setFilteredPostings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [accountOptions, setAccountOptions] = useState(["All"]);
  const [filters, setFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState(null);
  const [hoveredRowId, setHoveredRowId] = useState(null);
  const [userLabId, setUserLabId] = useState(null);

  // =========================================================
  // GET USER LAB ID
  // =========================================================
  useEffect(() => {
    const labId = localStorage.getItem("userLabId");

    if (labId) {
      setUserLabId(labId);
    } else {
      setError("User Lab ID not found. Please log in again.");
    }
  }, []);

  // =========================================================
  // FORMATTERS
  // =========================================================
  const formatInvoiceNumber = (number) => {
    if (!number) return "N/A";
    return String(number).padStart(6, "0");
  };

  const formatCreditMemoNumber = (number) => {
    if (!number) return "N/A";
    return String(number).replace("CR-", "");
  };

  // =========================================================
  // NORMALIZERS
  // =========================================================
  const normalizeInvoice = (invoice) => {
    try {
      const invoiceDate = invoice.invoiceDate
        ? new Date(invoice.invoiceDate)
        : new Date();

      return {
        id: invoice._id,
        postDate: invoiceDate.toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
        }),
        type: "Debit",
        accountId: invoice.accountId || "N/A",
        accountName:
          invoice.accountHolder ||
          (invoice.patientFirstName && invoice.patientLastName
            ? `${invoice.patientFirstName} ${invoice.patientLastName}`
            : "Unknown Account"),
        amount: parseFloat(invoice.total) || 0,
        referenceNo: invoice.invoiceNumber ? [invoice.invoiceNumber] : [],
        originalDate: invoiceDate,
        originalData: invoice,
      };
    } catch {
      return null;
    }
  };

  const normalizePayment = (payment, validInvoiceIds, validInvoiceNumbers) => {
    try {
      let isValid = false;

      if (payment.appliedInvoices && payment.appliedInvoices.length > 0) {
        for (const appliedInvoice of payment.appliedInvoices) {
          const invoiceId = appliedInvoice.invoiceId;
          const invoiceNumber = appliedInvoice.invoiceNumber;
          if (
            (invoiceId && validInvoiceIds.has(invoiceId)) ||
            (invoiceNumber && validInvoiceNumbers.has(invoiceNumber))
          ) {
            isValid = true;
            break;
          }
        }
      }

      if (!isValid && (payment.invoiceId || payment.invoiceNumber)) {
        if (
          (payment.invoiceId && validInvoiceIds.has(payment.invoiceId)) ||
          (payment.invoiceNumber &&
            validInvoiceNumbers.has(payment.invoiceNumber))
        ) {
          isValid = true;
        }
      }

      if (!isValid) return null;

      const paymentDate = payment.paymentDate
        ? new Date(payment.paymentDate)
        : new Date();

      const totalAmount =
        payment.appliedInvoices?.reduce(
          (sum, inv) => sum + parseFloat(inv.appliedAmount || 0),
          0
        ) ||
        parseFloat(payment.amountReceived) ||
        0;

      const invoiceNumbers = [];
      if (payment.appliedInvoices && payment.appliedInvoices.length > 0) {
        payment.appliedInvoices.forEach((inv) => {
          if (inv.invoiceNumber) invoiceNumbers.push(inv.invoiceNumber);
        });
      }

      return {
        id: payment._id,
        postDate: paymentDate.toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
        }),
        type: "Payment",
        accountId: payment.accountId || "N/A",
        accountName: payment.accountName || "Unknown Account",
        amount: totalAmount,
        referenceNo:
          invoiceNumbers.length > 0
            ? invoiceNumbers
            : payment.referenceNo
            ? [payment.referenceNo]
            : [],
        originalDate: paymentDate,
        originalData: payment,
      };
    } catch {
      return null;
    }
  };

  const normalizeCredit = (credit, validInvoiceIds, validInvoiceNumbers) => {
    try {
      let isValid = false;

      if (credit.appliedInvoices && credit.appliedInvoices.length > 0) {
        for (const appliedInvoice of credit.appliedInvoices) {
          const invoiceId = appliedInvoice.invoiceId;
          const invoiceNumber = appliedInvoice.invoiceNumber;
          if (
            (invoiceId && validInvoiceIds.has(invoiceId)) ||
            (invoiceNumber && validInvoiceNumbers.has(invoiceNumber))
          ) {
            isValid = true;
            break;
          }
        }
      }

      if (!isValid && (credit.invoiceId || credit.invoiceNumber)) {
        if (
          (credit.invoiceId && validInvoiceIds.has(credit.invoiceId)) ||
          (credit.invoiceNumber && validInvoiceNumbers.has(credit.invoiceNumber))
        ) {
          isValid = true;
        }
      }

      if (!isValid) return null;

      const creditDate = credit.creditDate
        ? new Date(credit.creditDate)
        : credit.createdAt
        ? new Date(credit.createdAt)
        : new Date();

      const appliedInvoices = credit.appliedInvoices || [];
      const referenceNumbers = appliedInvoices
        .map((inv) => inv.invoiceNumber)
        .filter(Boolean);

      const references =
        referenceNumbers.length > 0
          ? referenceNumbers
          : credit.creditMemoId
          ? [credit.creditMemoId]
          : [];

      return {
        id: credit._id,
        creditMemoId: credit.creditMemoId,
        postDate: creditDate.toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
        }),
        type: "Credit",
        accountId: credit.accountId || "N/A",
        accountName: credit.accountName || "Unknown Account",
        amount: parseFloat(credit.amount) || 0,
        referenceNo: references,
        originalDate: creditDate,
        originalData: credit,
      };
    } catch {
      return null;
    }
  };

  // =========================================================
  // FETCH ALL DATA (once)
  // =========================================================
  useEffect(() => {
    if (!userLabId) return undefined;

    let cancelled = false;
    const controller = new AbortController();

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        // 1) INVOICES
        let invoices = [];
        const validInvoiceIds = new Set();
        const validInvoiceNumbers = new Set();

        try {
          const invoicesResponse = await axios.get(
            `${API_BASE}/invoices/invoices/all`,
            {
              params: { labId: userLabId },
              signal: controller.signal,
            }
          );

          invoices = invoicesResponse.data || [];

          invoices.forEach((invoice) => {
            if (invoice._id) validInvoiceIds.add(invoice._id);
            if (invoice.invoiceNumber)
              validInvoiceNumbers.add(invoice.invoiceNumber);
          });
        } catch (invoiceError) {
          if (!cancelled) {
            setError("Failed to fetch invoices. Please try again.");
          }
          setLoading(false);
          return;
        }

        if (cancelled) return;

        // 2) PAYMENTS
        let allPayments = [];
        try {
          const paymentsResponse = await axios.get(
            `${API_BASE}/receive-payments`,
            { signal: controller.signal }
          );
          allPayments = paymentsResponse.data || [];
        } catch {
          /* keep behavior */
        }

        if (cancelled) return;

        // 3) CREDIT MEMOS
        let allCreditMemos = [];
        try {
          const creditMemosResponse = await axios.get(
            `${API_BASE}/credit-memos`,
            { signal: controller.signal }
          );

          if (Array.isArray(creditMemosResponse.data)) {
            allCreditMemos = creditMemosResponse.data;
          } else if (
            creditMemosResponse.data?.data &&
            Array.isArray(creditMemosResponse.data.data)
          ) {
            allCreditMemos = creditMemosResponse.data.data;
          } else if (
            creditMemosResponse.data?.creditMemos &&
            Array.isArray(creditMemosResponse.data.creditMemos)
          ) {
            allCreditMemos = creditMemosResponse.data.creditMemos;
          }
        } catch {
          /* keep behavior */
        }

        if (cancelled) return;

        // 4) NORMALIZE
        const invoiceEntries = invoices.map(normalizeInvoice).filter(Boolean);

        const paymentEntries = allPayments
          .map((p) => normalizePayment(p, validInvoiceIds, validInvoiceNumbers))
          .filter(Boolean);

        const creditEntries = allCreditMemos
          .map((c) => normalizeCredit(c, validInvoiceIds, validInvoiceNumbers))
          .filter(Boolean);

        // 5) MERGE + SORT (newest first)
        const allEntries = [
          ...invoiceEntries,
          ...paymentEntries,
          ...creditEntries,
        ].sort((a, b) => b.originalDate - a.originalDate);

        if (cancelled) return;

        setAllPostings(allEntries);
        setFilteredPostings(allEntries);
        setCurrentPage(1);

        // 6) ACCOUNT OPTIONS
        const uniqueAccounts = [
          ...new Set(
            allEntries
              .map((item) => item.accountName)
              .filter(
                (name) =>
                  name && name !== "Unknown Account" && name !== "N/A"
              )
          ),
        ];
        setAccountOptions(["All", ...uniqueAccounts]);
      } catch (err) {
        if (!cancelled) setError("Failed to load posting entries.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();

    return () => {
      cancelled = true;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLabId]);

  // =========================================================
  // EXPORT HELPERS
  // =========================================================
  const buildExportRows = (entries) => {
    return entries.map((p) => {
      let displayAmount;

      if (p.type === "Debit" || p.type === "Credit") {
        displayAmount = p.amount || 0;
      } else if (p.type === "Payment") {
        displayAmount = -(p.amount || 0);
      } else {
        displayAmount = p.amount || 0;
      }

      let referenceText = "N/A";

      if (p.type === "Credit") {
        const creditMemoId =
          p.referenceNo?.[0] ||
          p.creditMemoId ||
          p.originalData?.creditMemoId;
        referenceText = formatCreditMemoNumber(creditMemoId) || "N/A";
      } else {
        const refs = p.referenceNo || [];
        if (refs.length > 0) {
          const formatted = refs.map((r) => formatInvoiceNumber(r));
          referenceText =
            p.type === "Payment"
              ? `Invoices: ${formatted.join(", ")}`
              : formatted.join(", ");
        }
      }

      return {
        "Post Date": p.postDate || "",
        Type: p.type || "",
        Account: p.accountName || "",
        Amount: displayAmount,
        Reference: referenceText,
        "Statement Date": "",
      };
    });
  };

  const downloadCSV = (rows, filename) => {
    if (!rows || rows.length === 0) {
      alert("No data to export.");
      return;
    }

    const headers = Object.keys(rows[0]);

    const escapeCell = (val) => {
      if (val === null || val === undefined) return "";
      const s = String(val);
      if (
        s.includes(",") ||
        s.includes('"') ||
        s.includes("\n") ||
        s.includes("\r")
      ) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };

    const csvLines = [
      headers.map(escapeCell).join(","),
      ...rows.map((row) => headers.map((h) => escapeCell(row[h])).join(",")),
    ];

    const csvContent = "\uFEFF" + csvLines.join("\r\n");
    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadExcel = (rows, filename) => {
    if (!rows || rows.length === 0) {
      alert("No data to export.");
      return;
    }

    try {
      const worksheet = XLSX.utils.json_to_sheet(rows);

      const colWidths = Object.keys(rows[0]).map((key) => {
        const maxLen = Math.max(
          key.length,
          ...rows.map((r) =>
            r[key] !== null && r[key] !== undefined ? String(r[key]).length : 0
          )
        );
        return { wch: Math.min(maxLen + 2, 50) };
      });
      worksheet["!cols"] = colWidths;

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Postings");

      XLSX.writeFile(workbook, filename);
    } catch (err) {
      console.error("Excel export failed:", err);
      alert("Failed to export Excel file.");
    }
  };

  // =========================================================
  // FILTER APPLY
  // =========================================================
  const handleFilterApply = (filterData) => {
    if (!filterData || Object.keys(filterData).length === 0) {
      setFilters({});
      setFilteredPostings(allPostings);
      setCurrentPage(1);
      return;
    }

    setFilters(filterData);

    let filtered = [...allPostings];

    if (filterData.startDate && filterData.endDate) {
      const start = new Date(filterData.startDate);
      const end = new Date(filterData.endDate);
      end.setHours(23, 59, 59, 999);

      filtered = filtered.filter((item) => {
        const itemDate = item.originalDate;
        return itemDate >= start && itemDate <= end;
      });
    }

    if (filterData.type && filterData.type !== "All") {
      filtered = filtered.filter((item) => item.type === filterData.type);
    }

    if (filterData.accounts && filterData.accounts.length > 0) {
      if (!filterData.accounts.includes("All")) {
        filtered = filtered.filter((item) =>
          filterData.accounts.includes(item.accountName)
        );
      }
    }

    setFilteredPostings(filtered);
    setCurrentPage(1);

    const fmt = filterData.format;

    if (fmt === "CSV" || fmt === "Excel") {
      const rows = buildExportRows(filtered);
      const dateTag = new Date().toISOString().slice(0, 10);
      const filename = `postings-${dateTag}.${fmt === "CSV" ? "csv" : "xlsx"}`;

      if (fmt === "CSV") {
        downloadCSV(rows, filename);
      } else {
        downloadExcel(rows, filename);
      }
    }

    if (fmt === "PDF") {
      setTimeout(() => window.print(), 300);
    }
  };

  // =========================================================
  // RESET FILTER
  // =========================================================
  const handleResetFilter = () => {
    setFilters({});
    setFilteredPostings(allPostings);
    setCurrentPage(1);
  };

  // =========================================================
  // MODAL HANDLERS
  // =========================================================
  const handleShowInvoiceModal = () => setShowInvoiceModal(true);
  const handleCloseInvoiceModal = () => setShowInvoiceModal(false);

  const handleShowFilterModel = () => setShowFilterModel(true);
  const handleCloseFilterModel = () => setShowFilterModel(false);

  const handleShowCreditMemoModal = () => setShowCreditMemoModal(true);
  const handleCloseCreditMemoModal = () => setShowCreditMemoModal(false);

  const handleShowReceivePaymentModal = () => setShowReceivePaymentModal(true);
  const handleCloseReceivePaymentModal = () =>
    setShowReceivePaymentModal(false);

  // =========================================================
  // CELL RENDERERS
  // =========================================================
  const renderReference = (posting) => {
    try {
      if (posting.type === "Credit") {
        const creditMemoId =
          posting.referenceNo?.[0] ||
          posting.creditMemoId ||
          posting.originalData?.creditMemoId;

        const formatted = formatCreditMemoNumber(creditMemoId);

        if (formatted && formatted !== "N/A") {
          return (
            <Link
              to={`/credits/${posting.id}`}
              style={{
                textDecoration: "none",
                color: "#007bff",
                marginRight: "5px",
              }}
            >
              {formatted}
            </Link>
          );
        }

        return formatted || "N/A";
      }

      const refs = posting.referenceNo || [];

      if (refs.length === 0) return "N/A";

      const formattedLinks = refs
        .map((ref, index) => {
          const formatted = formatInvoiceNumber(ref);

          if (posting.type === "Payment") {
            return (
              <Link
                key={`${ref}-${index}`}
                to={`/payments/${posting.id}`}
                style={{
                  textDecoration: "none",
                  color: "#007bff",
                  marginRight: "5px",
                }}
              >
                {formatted}
              </Link>
            );
          }

          if (posting.type === "Debit") {
            return (
              <span key={`${ref}-${index}`} style={{ marginRight: "5px" }}>
                {formatted}
              </span>
            );
          }

          return null;
        })
        .filter(Boolean);

      if (formattedLinks.length === 0) return "N/A";

      let prefix = "";
      if (posting.type === "Payment") prefix = "Invoices: ";

      if (posting.type === "Payment" && formattedLinks.length > 1) {
        return (
          <>
            {prefix}
            {formattedLinks.map((link, idx) => (
              <React.Fragment key={idx}>
                {idx > 0 ? ", " : ""}
                {link}
              </React.Fragment>
            ))}
          </>
        );
      }

      return (
        <>
          {prefix}
          {formattedLinks[0]}
        </>
      );
    } catch {
      return "N/A";
    }
  };

  const renderTypeCell = (posting) => {
    try {
      if (posting.type === "Payment") {
        return (
          <Link
            to={`/payments/${posting.id}`}
            style={{
              textDecoration: "none",
              color: "#007bff",
              fontWeight: "500",
            }}
          >
            {posting.type}
          </Link>
        );
      }

      if (posting.type === "Credit") {
        return (
          <Link
            to={`/credits/${posting.id}`}
            style={{
              textDecoration: "none",
              color: "#007bff",
              fontWeight: "500",
            }}
          >
            {posting.type}
          </Link>
        );
      }

      return (
        <span style={{ color: "#333", fontWeight: "500" }}>{posting.type}</span>
      );
    } catch {
      return <span>Error</span>;
    }
  };

  const renderActionCell = (posting) => {
    if (hoveredRowId !== posting.id) return null;

    if (posting.type === "Payment" || posting.type === "Credit") {
      return (
        <FaTrash
          onClick={() => handleDelete(posting)}
          style={{
            color: "#dc3545",
            cursor: "pointer",
            fontSize: "16px",
          }}
          title="Delete"
        />
      );
    }

    return null;
  };

  // =========================================================
  // DELETE  (UPDATED)
  // =========================================================
  const getDeleteUrl = (posting) =>
    posting.type === "Payment"
      ? `${API_BASE}/payments/${posting.id}`
      : `${API_BASE}/credit-memos/${posting.id}`;

  const getRemoveInvoicesUrl = (posting) =>
    posting.type === "Payment"
      ? `${API_BASE}/payments/${posting.id}/remove-invoices`
      : `${API_BASE}/credit-memos/${posting.id}/remove-invoices`;

  // Poori entry list se hata do
  const removePostingFromState = (postingId) => {
    setAllPostings((prev) => prev.filter((item) => item.id !== postingId));
    setFilteredPostings((prev) => prev.filter((item) => item.id !== postingId));
  };

  // Sirf selected invoices hata kar entry update karo (baqi invoices wesi hi)
  const patchPostingAfterPartialDelete = (posting, removedNumbers) => {
    const removed = removedNumbers.map(String);

    const patch = (item) => {
      if (item.id !== posting.id) return item;

      const remainingApplied = (item.originalData?.appliedInvoices || []).filter(
        (a) => !removed.includes(String(a.invoiceNumber))
      );

      const newAmount = remainingApplied.reduce(
        (sum, a) => sum + parseFloat(a.appliedAmount || 0),
        0
      );

      return {
        ...item,
        amount: newAmount,
        referenceNo: remainingApplied
          .map((a) => a.invoiceNumber)
          .filter(Boolean),
        originalData: {
          ...item.originalData,
          appliedInvoices: remainingApplied,
        },
      };
    };

    setAllPostings((prev) => prev.map(patch));
    setFilteredPostings((prev) => prev.map(patch));
  };

  const handleDelete = async (posting) => {
    if (posting.type !== "Payment" && posting.type !== "Credit") {
      alert("Delete not supported for this type yet.");
      return;
    }

    const appliedInvoices = posting.originalData?.appliedInvoices || [];

    // Ek se zyada invoices -> selection modal kholo
    if (appliedInvoices.length > 1) {
      setDeleteTarget(posting);
      return;
    }

    // 0 ya 1 invoice -> purana simple confirm + full delete
    const confirmDelete = window.confirm(
      `Are you sure you want to delete this ${posting.type}?`
    );
    if (!confirmDelete) return;

    try {
      await axios.delete(getDeleteUrl(posting));
      removePostingFromState(posting.id);
      alert(`${posting.type} deleted successfully ✅`);
    } catch {
      alert(`Failed to delete ${posting.type} ❌`);
    }
  };

  const handleConfirmDeleteInvoices = async (selectedInvoiceNumbers) => {
    const posting = deleteTarget;
    if (!posting || selectedInvoiceNumbers.length === 0) return;

    const appliedCount = (posting.originalData?.appliedInvoices || []).length;
    const deleteAll = selectedInvoiceNumbers.length === appliedCount;

    const confirmMsg = deleteAll
      ? `All invoices are selected. The whole ${posting.type} will be deleted. Continue?`
      : `Remove ${selectedInvoiceNumbers.length} selected invoice(s) from this ${posting.type}?`;

    if (!window.confirm(confirmMsg)) return;

    setDeleting(true);

    try {
      if (deleteAll) {
        await axios.delete(getDeleteUrl(posting));
        removePostingFromState(posting.id);
        alert(`${posting.type} deleted successfully ✅`);
      } else {
        await axios.put(getRemoveInvoicesUrl(posting), {
          invoiceNumbers: selectedInvoiceNumbers,
        });
        patchPostingAfterPartialDelete(posting, selectedInvoiceNumbers);
        alert("Selected invoices removed successfully ✅");
      }

      setDeleteTarget(null);
    } catch (err) {
      alert(
        err?.response?.data?.message ||
          `Failed to delete from ${posting.type} ❌`
      );
    } finally {
      setDeleting(false);
    }
  };

  // =========================================================
  // STYLES
  // =========================================================
  const cellStyle = {
    textAlign: "left",
    borderBottom: "1px solid #ddd",
    color: "#333",
  };

  const linkStyle = {
    textDecoration: "none",
    color: "#004aad",
    marginRight: "5px",
  };

  // =========================================================
  // PAGINATION (client-side slice)
  // =========================================================
  const totalEntries = filteredPostings.length;
  const totalPages = Math.max(1, Math.ceil(totalEntries / ENTRIES_PER_PAGE));

  const indexOfLastEntry = currentPage * ENTRIES_PER_PAGE;
  const indexOfFirstEntry = indexOfLastEntry - ENTRIES_PER_PAGE;

  const currentEntries = filteredPostings.slice(
    indexOfFirstEntry,
    indexOfLastEntry
  );

  const handlePageChange = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // =========================================================
  // LOADING
  // =========================================================
  if (loading && allPostings.length === 0) {
    return (
      <>
        <Header />
        <Navvbar />
        <div style={{ textAlign: "center", padding: "50px", minHeight: "60vh" }}>
          <h4>Loading posting entries...</h4>

          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
        <div
          className="posting-footer-wrapper"
          style={{ width: "95%", margin: "0 auto", padding: "0px" }}
        >
          <Footer />
        </div>
      </>
    );
  }

  // =========================================================
  // NO LAB ID
  // =========================================================
  if (!userLabId) {
    return (
      <>
        <Header />
        <Navvbar />
        <div style={{ textAlign: "center", padding: "50px", minHeight: "60vh" }}>
          <h4>Please log in to view posting entries</h4>
        </div>
        <div
          className="posting-footer-wrapper"
          style={{ width: "95%", margin: "0 auto", padding: "0px" }}
        >
          <Footer />
        </div>
      </>
    );
  }

  // =========================================================
  // MAIN UI
  // =========================================================
  return (
    <>
      <Header />

      <Navvbar />
      <br />

      <div
        className="posting-page"
        style={{
          width: "95%",
          margin: "0 auto",
          padding: "0px",
          minHeight: "calc(100vh - 200px)",
        }}
      >
        <div className="row posting-main-row">
          <FinanceSidebar variant="posting" />

          <div className="col-md-10 posting-content">
            <h2 className="posting-title">Posting Entries</h2>

            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}

            {/* FILTER AREA */}
            <div
              className="posting-filter-area"
              style={{
                backgroundColor: "#f4f4f4",
                padding: "20px",
                marginTop: "20px",
                borderTop: "2px solid #004aad",
              }}
            >
              <div
                className="posting-toolbar"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px",
                }}
              >
                <div></div>

                <div
                  className="posting-buttons"
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <Button
                    variant="primary"
                    id="filter_postings_button"
                    className="mr-2 posting-filter-button"
                    onClick={handleShowFilterModel}
                    style={{
                      backgroundColor: "#004aad",
                      color: "white",
                      borderColor: "#004aad",
                      marginRight: "10px",
                    }}
                  >
                    <FaRedo /> Filter
                  </Button>

                  <ButtonGroup>
                    <Dropdown>
                      <Dropdown.Toggle
                        className="posting-create-button"
                        style={{ backgroundColor: "#004aad", color: "white" }}
                        id="dropdown-basic"
                      >
                        Create New <span className="caret"></span>
                      </Dropdown.Toggle>

                      <Dropdown.Menu>
                        <Dropdown.Item onClick={handleShowReceivePaymentModal}>
                          Payment
                        </Dropdown.Item>

                        <Dropdown.Item onClick={handleShowInvoiceModal}>
                          Invoice
                        </Dropdown.Item>

                        <Dropdown.Item onClick={handleShowCreditMemoModal}>
                          Credit
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </ButtonGroup>
                </div>
              </div>

              <div
                className="posting-count-top"
                style={{
                  marginBottom: "15px",
                  fontSize: "16px",
                  fontWeight: "500",
                  color: "#333",
                }}
              >
                Showing {totalEntries === 0 ? 0 : indexOfFirstEntry + 1} to{" "}
                {Math.min(indexOfLastEntry, totalEntries)} of {totalEntries}{" "}
                entries
              </div>
            </div>

            {/* TABLE */}
            <div className="posting-table-wrapper">
              <Table
                hover
                id="postings"
                className="posting-table"
                style={{ borderCollapse: "collapse", width: "100%" }}
              >
                <thead>
                  <tr style={{ fontSize: "18px" }}>
                    {[
                      "Post Date",
                      "Type",
                      "Account",
                      "Amount",
                      "Reference",
                      "Statement Date",
                      "Action",
                    ].map((header, index) => (
                      <th
                        key={index}
                        style={{
                          backgroundColor: "#f4f4f4",
                          border: "none",
                          padding: "12px 16px",
                          textAlign: "left",
                          color: "#333",
                          fontWeight: "600",
                        }}
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody style={{ backgroundColor: "#fff" }}>
                  {currentEntries.length > 0 ? (
                    currentEntries.map((posting, index) => {
                      let displayAmount;

                      if (posting.type === "Debit") {
                        displayAmount = posting.amount || 0;
                      } else if (posting.type === "Credit") {
                        displayAmount = posting.amount || 0;
                      } else if (posting.type === "Payment") {
                        displayAmount = -(posting.amount || 0);
                      } else {
                        displayAmount = posting.amount || 0;
                      }

                      return (
                        <tr
                          key={`${posting.id}-${index}`}
                          onMouseEnter={() => setHoveredRowId(posting.id)}
                          onMouseLeave={() => setHoveredRowId(null)}
                          style={{
                            backgroundColor:
                              hoveredRowId === posting.id ? "#f9f9f9" : "#fff",
                            cursor: "pointer",
                          }}
                        >
                          <td style={cellStyle}>{posting.postDate}</td>

                          <td style={cellStyle}>{renderTypeCell(posting)}</td>

                          <td style={cellStyle}>
                            {posting.accountId && posting.accountId !== "N/A" ? (
                              <Link
                                to={`/tabsWorking/${posting.accountId}`}
                                style={linkStyle}
                              >
                                {posting.accountName}
                              </Link>
                            ) : (
                              <span>{posting.accountName}</span>
                            )}
                          </td>

                          <td style={cellStyle}>{formatAmount(displayAmount)}</td>

                          <td style={cellStyle}>{renderReference(posting)}</td>

                          <td style={cellStyle}></td>

                          <td>{renderActionCell(posting)}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td
                        colSpan="7"
                        style={{ textAlign: "center", padding: "40px" }}
                      >
                        {allPostings.length === 0 ? (
                          <div>
                            <h5>No posting entries found</h5>
                            <p className="text-muted">
                              Try creating a new invoice, payment, or credit
                              memo.
                            </p>
                          </div>
                        ) : (
                          <div>
                            <h5>No entries match your filters</h5>
                            <Button
                              variant="link"
                              onClick={handleResetFilter}
                              style={{ color: "#004aad" }}
                            >
                              Clear filters to see all entries
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            {/* BOTTOM COUNT */}
            <div
              className="posting-bottom-count"
              style={{
                backgroundColor: "#f4f4f4",
                padding: "20px",
                marginTop: "-20px",
                borderTop: "2px solid #004aad",
                color: "#333",
              }}
            >
              Showing {totalEntries === 0 ? 0 : indexOfFirstEntry + 1} to{" "}
              {Math.min(indexOfLastEntry, totalEntries)} of {totalEntries}{" "}
              entries
            </div>

            {/* PAGINATION */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-3 posting-pagination">
                <Pagination>
                  <Pagination.Prev
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <AiOutlineLeft />
                  </Pagination.Prev>

                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNumber;

                    if (totalPages <= 5) {
                      pageNumber = i + 1;
                    } else if (currentPage <= 3) {
                      pageNumber = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNumber = totalPages - 4 + i;
                    } else {
                      pageNumber = currentPage - 2 + i;
                    }

                    if (pageNumber < 1 || pageNumber > totalPages) return null;

                    return (
                      <Pagination.Item
                        key={pageNumber}
                        active={pageNumber === currentPage}
                        onClick={() => handlePageChange(pageNumber)}
                      >
                        {pageNumber}
                      </Pagination.Item>
                    );
                  })}

                  <Pagination.Next
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    <AiOutlineRight />
                  </Pagination.Next>
                </Pagination>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* FOOTER */}
      <div
        className="posting-footer-wrapper"
        style={{ width: "95%", margin: "20px auto 0", padding: "0px" }}
      >
        <Footer />
      </div>

      {/* MODALS */}
      <InvoiceModal show={showInvoiceModal} handleClose={handleCloseInvoiceModal} />

      <FilterModel
        show={showFilterModel}
        handleClose={handleCloseFilterModel}
        onFilterApply={handleFilterApply}
        accountOptions={accountOptions}
        typeOptions={["All", "Debit", "Payment", "Credit"]}
      />

      <CreditMemo
        show={showCreditMemoModal}
        handleClose={handleCloseCreditMemoModal}
      />

      <Payment
        show={showReceivePaymentModal}
        handleClose={handleCloseReceivePaymentModal}
      />

      {/* NEW: select invoices to delete */}
      <DeleteInvoicesModal
        show={!!deleteTarget}
        posting={deleteTarget}
        deleting={deleting}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDeleteInvoices}
      />

      {/* ============ RESPONSIVE CSS ============ */}
      <style>{`
        /* =====================================================
           CREATE NEW DROPDOWN BOX (Payment / Invoice / Credit)
           Sab screen sizes par same clean look
           ===================================================== */
        .posting-page .posting-buttons .dropdown-menu {
          min-width: 130px !important;
          padding: 0 !important;
          margin-top: 2px !important;
          background-color: #fff !important;
          border: 1px solid #d9d9d9 !important;
          border-radius: 6px !important;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12) !important;
          overflow: hidden !important;
          font-size: 16px !important;
          z-index: 1050 !important;
        }

        .posting-page .posting-buttons .dropdown-item {
          display: block !important;
          width: 100% !important;
          padding: 10px 16px !important;
          font-size: 16px !important;
          line-height: 1.4 !important;
          color: #333 !important;
          background-color: #fff !important;
          border-bottom: 1px solid #e6e6e6 !important;
          white-space: nowrap !important;
          cursor: pointer !important;
        }

        .posting-page .posting-buttons .dropdown-item:last-child {
          border-bottom: none !important;
        }

        .posting-page .posting-buttons .dropdown-item:hover,
        .posting-page .posting-buttons .dropdown-item:focus,
        .posting-page .posting-buttons .dropdown-item:active {
          background-color: #f1f1f1 !important;
          color: #222 !important;
        }

        @media (min-width: 768px) {
          .posting-page .posting-create-button {
            font-size: 16px !important;
            line-height: 1.5 !important;
            padding: 6px 12px !important;
            height: 38px !important;
            min-height: 38px !important;
            border-radius: 0.375rem !important;
          }
        }

        @media (max-width: 767px) {
          .posting-page {
            width: 96% !important;
            max-width: 96% !important;
            margin-left: auto !important;
            margin-right: auto !important;
            padding: 0 !important;
            overflow: visible !important;
            min-height: auto !important;
          }

          .posting-page .posting-main-row {
            display: flex !important;
            flex-direction: row !important;
            flex-wrap: nowrap !important;
            width: 100% !important;
            max-width: 100% !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
            align-items: flex-start !important;
          }

          .posting-page .finance-sidebar {
            display: block !important;
            flex: 0 0 15% !important;
            width: 15% !important;
            max-width: 15% !important;
            min-width: 0 !important;
            padding-left: 1px !important;
            padding-right: 2px !important;
            margin: 0 !important;
            border-right: 1px solid #004aad !important;
            overflow: visible !important;
          }

          .posting-page .finance-title {
            font-size: 8px !important;
            line-height: 1 !important;
            margin-left: 1px !important;
            margin-top: 4px !important;
            margin-bottom: 8px !important;
            padding: 0 !important;
            white-space: nowrap !important;
          }

          .posting-page .finance-link-wrapper {
            margin: 0 0 5px 0 !important;
            padding: 0 !important;
            line-height: 1 !important;
          }

          .posting-page .finance-link {
            display: inline-block !important;
            font-size: 5px !important;
            line-height: 1 !important;
            margin-left: 1px !important;
            margin-bottom: 3px !important;
            padding: 0 !important;
            white-space: nowrap !important;
          }

          .posting-page .posting-content {
            flex: 0 0 85% !important;
            width: 85% !important;
            max-width: 85% !important;
            min-width: 0 !important;
            padding-left: 3px !important;
            padding-right: 0 !important;
            margin: 0 !important;
            overflow: visible !important;
          }

          .posting-page .posting-title {
            font-size: 10px !important;
            line-height: 1 !important;
            margin-top: 4px !important;
            margin-bottom: 5px !important;
            padding: 0 !important;
            white-space: nowrap !important;
          }

          .posting-page .posting-content .alert {
            font-size: 6px !important;
            line-height: 1.1 !important;
            padding: 3px !important;
            margin: 3px 0 !important;
          }

          .posting-page .posting-filter-area {
            width: 100% !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
            padding: 4px !important;
            margin-top: 4px !important;
            border-top: 1px solid #004aad !important;
            overflow: visible !important;
          }

          .posting-page .posting-toolbar {
            display: flex !important;
            flex-direction: row !important;
            flex-wrap: nowrap !important;
            width: 100% !important;
            justify-content: flex-end !important;
            align-items: center !important;
            margin-bottom: 4px !important;
            min-height: 17px !important;
          }

          .posting-page .posting-buttons {
            display: flex !important;
            flex-direction: row !important;
            flex-wrap: nowrap !important;
            width: auto !important;
            max-width: 100% !important;
            justify-content: flex-end !important;
            align-items: center !important;
            gap: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
          }

          .posting-page .posting-filter-button {
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            flex: 0 0 auto !important;
            flex-shrink: 0 !important;
            font-size: 5px !important;
            line-height: 1 !important;
            padding: 2px 3px !important;
            width: auto !important;
            height: 16px !important;
            min-height: 16px !important;
            margin: 0 1px 0 0 !important;
            border-radius: 2px !important;
            white-space: nowrap !important;
          }

          .posting-page .posting-filter-button svg {
            width: 6px !important;
            height: 6px !important;
            margin-right: 1px !important;
            flex-shrink: 0 !important;
          }

          .posting-page .posting-buttons .btn-group {
            display: inline-flex !important;
            flex: 0 0 auto !important;
            flex-shrink: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
          }

          .posting-page .posting-create-button {
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            flex: 0 0 auto !important;
            flex-shrink: 0 !important;
            font-size: 5px !important;
            line-height: 1 !important;
            padding: 2px 3px !important;
            width: auto !important;
            height: 16px !important;
            min-height: 16px !important;
            margin: 0 !important;
            border-radius: 2px !important;
            white-space: nowrap !important;
          }

          .posting-page .posting-buttons .dropdown {
            display: inline-flex !important;
            flex: 0 0 auto !important;
            margin: 0 !important;
            padding: 0 !important;
          }

          /* Dropdown box mobile par bhi readable */
          .posting-page .posting-buttons .dropdown-menu {
            min-width: 120px !important;
            font-size: 14px !important;
            padding: 0 !important;
          }

          .posting-page .posting-buttons .dropdown-item {
            font-size: 14px !important;
            padding: 9px 14px !important;
            line-height: 1.4 !important;
          }

          .posting-page .posting-count-top {
            font-size: 5px !important;
            line-height: 1 !important;
            margin-bottom: 3px !important;
            white-space: nowrap !important;
          }

          .posting-page .posting-table-wrapper {
            display: block !important;
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: visible !important;
          }

          .posting-page .posting-table {
            display: table !important;
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
            margin: 0 !important;
            table-layout: fixed !important;
            border-collapse: collapse !important;
            font-size: 5px !important;
            box-sizing: border-box !important;
          }

          .posting-page .posting-table thead { width: 100% !important; }

          .posting-page .posting-table thead tr {
            width: 100% !important;
            font-size: 5px !important;
          }

          .posting-page .posting-table thead th {
            font-size: 5px !important;
            padding: 2px 1px !important;
            line-height: 1 !important;
            font-weight: 600 !important;
            vertical-align: middle !important;
            text-align: left !important;
            white-space: normal !important;
            word-break: break-all !important;
            overflow-wrap: anywhere !important;
            overflow: hidden !important;
            box-sizing: border-box !important;
          }

          .posting-page .posting-table tbody { width: 100% !important; }

          .posting-page .posting-table tbody tr {
            width: 100% !important;
            height: auto !important;
          }

          .posting-page .posting-table tbody td {
            font-size: 5px !important;
            padding: 2px 1px !important;
            line-height: 1.05 !important;
            vertical-align: middle !important;
            text-align: left !important;
            white-space: normal !important;
            word-break: break-all !important;
            overflow-wrap: anywhere !important;
            overflow: hidden !important;
            height: auto !important;
            box-sizing: border-box !important;
          }

          .posting-page .posting-table th:nth-child(1),
          .posting-page .posting-table td:nth-child(1) {
            width: 12% !important;
            max-width: 12% !important;
          }

          .posting-page .posting-table th:nth-child(2),
          .posting-page .posting-table td:nth-child(2) {
            width: 9% !important;
            max-width: 9% !important;
          }

          .posting-page .posting-table th:nth-child(3),
          .posting-page .posting-table td:nth-child(3) {
            width: 21% !important;
            max-width: 21% !important;
          }

          .posting-page .posting-table th:nth-child(4),
          .posting-page .posting-table td:nth-child(4) {
            width: 12% !important;
            max-width: 12% !important;
          }

          .posting-page .posting-table th:nth-child(5),
          .posting-page .posting-table td:nth-child(5) {
            width: 17% !important;
            max-width: 17% !important;
          }

          .posting-page .posting-table th:nth-child(6),
          .posting-page .posting-table td:nth-child(6) {
            width: 19% !important;
            max-width: 19% !important;
          }

          .posting-page .posting-table th:nth-child(7),
          .posting-page .posting-table td:nth-child(7) {
            width: 10% !important;
            max-width: 10% !important;
          }

          .posting-page .posting-table a {
            display: inline !important;
            font-size: 5px !important;
            line-height: 1.05 !important;
            margin-right: 1px !important;
            white-space: normal !important;
            word-break: break-all !important;
            overflow-wrap: anywhere !important;
          }

          .posting-page .posting-table span {
            font-size: 5px !important;
            line-height: 1.05 !important;
            white-space: normal !important;
            word-break: break-all !important;
            overflow-wrap: anywhere !important;
          }

          .posting-page .posting-table svg {
            width: 6px !important;
            height: 6px !important;
            max-width: 6px !important;
            max-height: 6px !important;
          }

          .posting-page .posting-table td[colspan="7"] {
            width: 100% !important;
            max-width: 100% !important;
            font-size: 6px !important;
            padding: 10px 2px !important;
            text-align: center !important;
          }

          .posting-page .posting-table td[colspan="7"] h5 {
            font-size: 7px !important;
            margin-bottom: 3px !important;
          }

          .posting-page .posting-table td[colspan="7"] p {
            font-size: 5px !important;
            margin-bottom: 2px !important;
          }

          .posting-page .posting-table td[colspan="7"] button {
            font-size: 5px !important;
            padding: 1px 2px !important;
          }

          .posting-page .posting-bottom-count {
            width: 100% !important;
            font-size: 5px !important;
            line-height: 1 !important;
            padding: 4px !important;
            margin-top: 0 !important;
            border-top: 1px solid #004aad !important;
            box-sizing: border-box !important;
            white-space: nowrap !important;
          }

          .posting-page .posting-pagination {
            margin-top: 4px !important;
            width: 100% !important;
          }

          .posting-page .posting-pagination .pagination {
            margin-bottom: 4px !important;
          }

          .posting-page .posting-pagination .page-link {
            font-size: 6px !important;
            line-height: 1 !important;
            padding: 2px 3px !important;
          }

          .posting-footer-wrapper {
            width: 96% !important;
            margin-left: auto !important;
            margin-right: auto !important;
            margin-top: 15px !important;
          }
        }

        @media (max-width: 480px) {
          .posting-page { width: 98% !important; max-width: 98% !important; }

          .posting-page .finance-sidebar {
            flex: 0 0 14% !important;
            width: 14% !important;
            max-width: 14% !important;
            padding-left: 1px !important;
            padding-right: 1px !important;
          }

          .posting-page .finance-title {
            font-size: 7px !important;
            margin-left: 0 !important;
            margin-top: 3px !important;
            margin-bottom: 7px !important;
          }

          .posting-page .finance-link {
            font-size: 4px !important;
            margin-left: 0 !important;
            margin-bottom: 2px !important;
          }

          .posting-page .posting-content {
            flex: 0 0 86% !important;
            width: 86% !important;
            max-width: 86% !important;
            padding-left: 2px !important;
            padding-right: 0 !important;
          }

          .posting-page .posting-title {
            font-size: 9px !important;
            margin-top: 3px !important;
            margin-bottom: 4px !important;
          }

          .posting-page .posting-filter-area {
            padding: 3px !important;
            margin-top: 3px !important;
          }

          .posting-page .posting-toolbar {
            margin-bottom: 3px !important;
            min-height: 15px !important;
          }

          .posting-page .posting-buttons {
            gap: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
          }

          .posting-page .posting-filter-button {
            font-size: 4px !important;
            height: 14px !important;
            min-height: 14px !important;
            padding: 1px 2px !important;
            margin: 0 1px 0 0 !important;
          }

          .posting-page .posting-filter-button svg {
            width: 5px !important;
            height: 5px !important;
            margin-right: 1px !important;
          }

          .posting-page .posting-create-button {
            font-size: 4px !important;
            height: 14px !important;
            min-height: 14px !important;
            padding: 1px 2px !important;
            margin: 0 !important;
          }

          /* Dropdown box chhoti screen par bhi readable */
          .posting-page .posting-buttons .dropdown-menu {
            min-width: 115px !important;
            font-size: 13px !important;
          }

          .posting-page .posting-buttons .dropdown-item {
            font-size: 13px !important;
            padding: 8px 12px !important;
          }

          .posting-page .posting-count-top {
            font-size: 4px !important;
            margin-bottom: 2px !important;
          }

          .posting-page .posting-table { font-size: 4px !important; }

          .posting-page .posting-table thead th {
            font-size: 4px !important;
            padding: 2px 1px !important;
            line-height: 1 !important;
          }

          .posting-page .posting-table tbody td {
            font-size: 4px !important;
            padding: 2px 1px !important;
            line-height: 1 !important;
          }

          .posting-page .posting-table a,
          .posting-page .posting-table span {
            font-size: 4px !important;
            line-height: 1 !important;
          }

          .posting-page .posting-table svg {
            width: 5px !important;
            height: 5px !important;
          }

          .posting-page .posting-bottom-count {
            font-size: 4px !important;
            padding: 3px !important;
          }

          .posting-page .posting-pagination .page-link {
            font-size: 5px !important;
            padding: 1px 3px !important;
          }

          .posting-footer-wrapper {
            width: 98% !important;
            margin-top: 10px !important;
          }
        }

        @media (max-width: 360px) {
          .posting-page { width: 99% !important; max-width: 99% !important; }

          .posting-page .finance-sidebar {
            flex: 0 0 13% !important;
            width: 13% !important;
            max-width: 13% !important;
          }

          .posting-page .posting-content {
            flex: 0 0 87% !important;
            width: 87% !important;
            max-width: 87% !important;
            padding-left: 1px !important;
          }

          .posting-page .finance-title { font-size: 6px !important; }
          .posting-page .finance-link { font-size: 3.5px !important; }
          .posting-page .posting-title { font-size: 8px !important; }

          .posting-page .posting-buttons { gap: 0 !important; }

          .posting-page .posting-filter-button,
          .posting-page .posting-create-button {
            font-size: 3.5px !important;
            height: 13px !important;
            min-height: 13px !important;
            padding: 1px 2px !important;
          }

          .posting-page .posting-filter-button { margin-right: 1px !important; }

          .posting-page .posting-filter-button svg {
            width: 4px !important;
            height: 4px !important;
          }

          .posting-page .posting-table thead th,
          .posting-page .posting-table tbody td {
            font-size: 3.5px !important;
            padding: 1px !important;
          }

          .posting-page .posting-table a,
          .posting-page .posting-table span {
            font-size: 3.5px !important;
          }

          .posting-page .posting-count-top,
          .posting-page .posting-bottom-count {
            font-size: 3.5px !important;
          }
        }
      `}</style>
    </>
  );
};

export default Posting;